import {clone} from 'lodash';
import React, {CSSProperties, FunctionComponent, useState} from 'react';
import Dropzone from 'react-dropzone';
import ReactMde from 'react-mde';
import 'react-mde/lib/styles/css/react-mde-all.css';
import {useDispatch} from 'react-redux';
import * as Showdown from 'showdown';
import {NotificationEditorContentProps} from '../..';
import {InputLabelEditor} from '../../../InputLabelEditor';
import {AvatarSelect, postImageUpload} from '../AvatarSelect';
import {Separator} from '../Separator';
import {
  appRoutesToRedirect as defaultRoutesToRedirect,
  avatrUrloptions,
  defaultAvatarUrl,
} from './constant';

const converter = new Showdown.Converter({
  tables: true,
  simplifiedAutoLink: true,
  strikethrough: true,
  tasklists: true,
});

const Styles: Record<string, CSSProperties> = {
  container: {
    position: 'relative',
    overflow: 'auto',
    height: 'calc(100vh - 300px)',
  },
  inputContainer: {
    marginBottom: 16,
  },
  flag: {
    margin: 8,
    height: 22,
    width: 22,
    borderRadius: 22,
    cursor: 'pointer',
  },
  selectedFlag: {
    width: 32,
    height: 32,
  },
  flagsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  avatarContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  image: {
    width: 40,
    height: 40,
    marginRight: 16,
  },
  dropZone: {
    width: 360,
    minHeight: 200,
    border: '1px dashed #4054CC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#4054CC',
    cursor: 'pointer',
  },
  label: {
    height: 32,
    border: '1px dashed #4054CC',
    borderRadius: 16,
    color: '#4054CC',
    paddingLeft: 8,
    paddingRight: 8,
    display: 'flex',
    alignItems: 'center',
    marginRight: 8,
  },
  labelContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 8,
  },
};

export const languagesFlagsRefs = {
  'fr-FR': 'fr',
  'en-GB': 'gb-eng',
  'es-ES': 'es',
};

export const LanguageSelector = ({
  onChange,
  selectedLanguage,
}: {
  onChange: (language: string) => void;
  selectedLanguage: string;
}) => {
  return (
    <div style={Styles.flagsContainer}>
      {Object.keys(languagesFlagsRefs).map(language => (
        <div onClick={() => onChange(language)}>
          <span
            style={{
              ...Styles.flag,
              ...(selectedLanguage === language && Styles.selectedFlag),
            }}
            key={language}
            className={`flag-icon flag-icon-${languagesFlagsRefs[language]} flag-icon-squared`}
          />
          {selectedLanguage === language && (
            <div
              style={{
                width: 40,
                backgroundColor: '#4054CC',
                height: 2,
                marginLeft: 4,
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export const NotificationEditorContent: FunctionComponent<
  NotificationEditorContentProps
> = ({data, onChange, isCreation, setErrorForSubmit, appRoutesToRedirect}) => {
  const dispatch = useDispatch();
  const [currentLabel, setCurrentLabel] = useState('');
  const [buttonLinkError, setButtonLinkError] = useState<false | string>(false);
  const [selectedLanguage, setSelectedLanguage] = useState('fr-FR');
  const [selectedTab, setSelectedTab] = React.useState<'write' | 'preview'>(
    'write',
  );
  const dataByLanguage = data.localization?.[selectedLanguage];  

  const checkForButtonLinkError = (buttonLink: string | undefined) => {
    if (
      buttonLink &&
      buttonLink?.indexOf('http') === -1 &&
      buttonLink?.indexOf(':') !== -1
    ) {
      // eslint-disable-next-line no-unused-expressions
      setErrorForSubmit?.(true);
      setButtonLinkError(buttonLink.split(':')[1].split('/')[0]);
    } else {
      // eslint-disable-next-line no-unused-expressions
      setErrorForSubmit?.(false);
    }
  };

  return (
    <div style={Styles.container}>
      <LanguageSelector
        onChange={language => setSelectedLanguage(language)}
        selectedLanguage={selectedLanguage}
      />
      <div style={Styles.inputContainer}>
        <InputLabelEditor title="AVATAR" />
        <div style={Styles.avatarContainer}>
          <img
            style={Styles.image}
            src={dataByLanguage?.avatarUrl || defaultAvatarUrl}
            alt=""
          />
          <AvatarSelect
            value={dataByLanguage?.avatarUrl || defaultAvatarUrl}
            onChange={imageUrl =>
              onChange(`localization.${selectedLanguage}.avatarUrl`, imageUrl)
            }
            options={avatrUrloptions}
          />
        </div>
      </div>
      <Separator />
      <div style={Styles.inputContainer}>
        <InputLabelEditor
          title="TITLE"
          isRequired={!isCreation}
          flag={selectedLanguage}
        />
        <textarea
          className="form-control"
          value={dataByLanguage?.title || ''}
          onChange={e =>
            onChange(`localization.${selectedLanguage}.title`, e.target.value)
          }
        />
      </div>
      <Separator />
      <div style={Styles.inputContainer}>
        <InputLabelEditor
          title="DESCRIPTION"
          isRequired={!isCreation}
          flag={selectedLanguage}
        />
        <ReactMde
          toolbarCommands={[
            ['bold', 'italic', 'strikethrough'],
            ['unordered-list', 'ordered-list'],
            ['link'],
          ]}
          value={dataByLanguage?.description || ''}
          onChange={value =>
            onChange(`localization.${selectedLanguage}.description`, value)
          }
          selectedTab={selectedTab}
          onTabChange={setSelectedTab}
          generateMarkdownPreview={markdown =>
            Promise.resolve(converter.makeHtml(markdown))
          }
        />
      </div>
      <Separator />
      <div style={Styles.inputContainer}>
        <InputLabelEditor title="IMAGE" flag={selectedLanguage} />
        <div style={{display: 'flex', flexDirection: 'row'}}>
          <Dropzone
            style={Styles.dropZone}
            onDrop={acceptedFiles => {
              const onSuccess = (imageUrl: string) => {
                onChange(`localization.${selectedLanguage}.imageUrl`, imageUrl);
              };
              dispatch(postImageUpload(acceptedFiles[0], onSuccess));
            }}>
            <div>
              {!dataByLanguage?.imageUrl ? (
                <span>Parcourir ou Drag n Drop</span>
              ) : (
                <img
                  src={dataByLanguage.imageUrl}
                  style={{maxWidth: 360}}
                  alt=""
                />
              )}
            </div>
          </Dropzone>
          {dataByLanguage?.imageUrl && (
            <div
              onClick={() =>
                onChange(`localization.${selectedLanguage}.imageUrl`, undefined)
              }
              style={{
                marginLeft: 16,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: 30,
                height: 30,
                backgroundColor: 'red',
                borderRadius: 30,
                cursor: 'pointer',
              }}>
              <i style={{color: 'white'}} className="fa fa-trash" />
            </div>
          )}
        </div>
      </div>
      <Separator />
      <div style={Styles.inputContainer}>
        <InputLabelEditor title="URL VIDEO" flag={selectedLanguage} />
        <input
          className="form-control"
          value={dataByLanguage?.socialEmbed?.youtube?.url || ''}
          placeholder="https://"
          onChange={e =>
            onChange(
              `localization.${selectedLanguage}.socialEmbed.youtube.url`,
              e.target.value,
            )
          }
        />
      </div>
      <Separator />
      <div style={Styles.inputContainer}>
        <InputLabelEditor
          title="CTA"
          flag={selectedLanguage}
          isRequired={!isCreation}
        />
        <input
          style={{marginBottom: 8}}
          className="form-control"
          value={dataByLanguage?.buttonTitle || ''}
          onChange={e =>
            onChange(
              `localization.${selectedLanguage}.buttonTitle`,
              e.target.value,
            )
          }
        />
        <div style={{display: 'flex', flexDirection: 'row'}}>
          <div style={{flex: 1, marginRight: 8}}>
            <select
              value={dataByLanguage?.buttonLink || ''}
              className="form-control"
              onChange={e => {
                checkForButtonLinkError(e.target.value);
                onChange(
                  `localization.${selectedLanguage}.buttonLink`,
                  e.target.value,
                );
              }}>
              <option value="">Select screen</option>
              {Object.values(appRoutesToRedirect || defaultRoutesToRedirect).map(route => (
                <option key={route.screenName} value={route.path}>
                  {route.screenName}
                </option>
              ))}
            </select>
          </div>
          <div style={{flex: 1, marginLeft: 8}}>
            <div className={`form-group ${buttonLinkError && 'has-error'}`}>
              <input
                onBlur={() =>
                  checkForButtonLinkError(dataByLanguage?.buttonLink)
                }
                className="form-control"
                value={dataByLanguage?.buttonLink}
                onChange={e => {
                  onChange(
                    `localization.${selectedLanguage}.buttonLink`,
                    e.target.value,
                  );
                  setButtonLinkError(false);
                }}
              />
              {buttonLinkError && (
                <p
                  style={{
                    color: 'red',
                  }}>
                  You need to specify
                  <b> {buttonLinkError} </b>
                  in CTA redirection
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      <Separator />
      <div style={Styles.inputContainer}>
        <InputLabelEditor title="MINI CTA" flag={selectedLanguage} />
        <input
          className="form-control"
          value={dataByLanguage?.userNotificationButtonTitle}
          onChange={e =>
            onChange(
              `localization.${selectedLanguage}.userNotificationButtonTitle`,
              e.target.value,
            )
          }
        />
      </div>
      <Separator />
      <div style={Styles.inputContainer}>
        <InputLabelEditor title="LABELS" flag={selectedLanguage} />
        <input
          value={currentLabel}
          className="form-control"
          onChange={e => setCurrentLabel(e.target.value)}
          onBlur={e => {
            if (e.target.value) {
              setCurrentLabel('');
              onChange(
                `localization.${selectedLanguage}.labels`,
                dataByLanguage?.labels
                  ? [...dataByLanguage.labels, e.target.value]
                  : [e.target.value],
              );
            }
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' && currentLabel) {
              setCurrentLabel('');
              onChange(
                `localization.${selectedLanguage}.labels`,
                dataByLanguage?.labels
                  ? [...dataByLanguage.labels, currentLabel]
                  : [currentLabel],
              );
            }
          }}
        />
        <div style={Styles.labelContainer}>
          {dataByLanguage?.labels?.map(label => (
            <div style={Styles.label}>
              {label}
              <i
                onClick={() => {
                  let labels = clone(dataByLanguage?.labels);
                  if (labels) {
                    const indexToRemove = labels.indexOf(label);
                    labels.splice(indexToRemove, 1);
                    onChange(`localization.${selectedLanguage}.labels`, labels);
                  }
                }}
                style={{marginLeft: 8, cursor: 'pointer'}}
                className="fa fa-times-circle"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/components/NotificationEditor/components/NotificationEditorContent/index.tsx